const TABLE_HEADER_PATTERN = /Item\s*\|\s*Estimated Cost\s*\|\s*Why it['’]s needed/i;
const SEPARATOR_ROW_PATTERN = /^\s*\|?\s*:?-{1,}:?\s*(?:\|\s*:?-{1,}:?\s*)+\|?\s*$/;
const COST_CELL_PATTERN = /\s*\|\s*([£$€]\s*[\d,]+(?:\.\d+)?(?:\s*[-–—]\s*[£$€]\s*[\d,]+(?:\.\d+)?)?)\s*\|\s*/;

// HAS GFM SEPARATOR
function hasGfmSeparator(markdown: string): boolean {
    /**
     * Returns true when a line of the markdown is already a GFM table
     * delimiter row, so the content can be left unchanged.
     */

    return markdown.split(/\r?\n/).some((line) => SEPARATOR_ROW_PATTERN.test(line));
}

// SPLIT CELLS
function splitCells(line: string): string[] {
    /**
     * Splits a pipe-delimited table row into trimmed cell values, dropping
     * the empty entries created by optional leading or trailing pipes.
     */

    const trimmed = line.trim();
    if (!trimmed) {
        return [];
    }

    const cells = trimmed.split("|").map((cell) => cell.trim());
    if (trimmed.startsWith("|")) {
        cells.shift();
    }
    if (trimmed.endsWith("|") && cells.length > 0) {
        cells.pop();
    }
    return cells;
}

// TO GFM TABLE
function toGfmTable(rows: string[][]): string {
    /**
     * Formats parsed rows as a GitHub-flavoured markdown table with wrapping
     * pipes and a dash separator under the header.
     */

    if (rows.length === 0) {
        return "";
    }

    const columnCount = Math.max(...rows.map((row) => row.length), 1);
    const normalised = rows.map((row) => {
        if (row.length > columnCount) {
            return [...row.slice(0, columnCount - 1), row.slice(columnCount - 1).join(" | ")];
        }
        const padded = [...row];
        while (padded.length < columnCount) {
            padded.push("");
        }
        return padded;
    });

    const formatRow = (cells: string[]): string => `| ${cells.join(" | ")} |`;
    const separator = `| ${Array(columnCount).fill("---").join(" | ")} |`;
    return [formatRow(normalised[0]), separator, ...normalised.slice(1).map(formatRow)].join("\n");
}

// SPLIT WHY FROM NEXT ITEM
function splitWhyFromNextItem(text: string): {why: string; nextItem: string} {
    /**
     * When concatenated rows omit the pipe between the reason and the next
     * item name, split on the last sentence boundary if the remainder looks
     * like a short title.
     */

    const match = text.match(/^([\s\S]*[.!?])\s+([A-Z0-9][\s\S]*)$/);
    if (match && match[2].length <= 120 && !match[2].includes("|")) {
        return {why: match[1].trim(), nextItem: match[2].trim()};
    }

    return {why: text.trim(), nextItem: ""};
}

// RECONSTRUCT CONCATENATED TABLE
function reconstructConcatenatedTable(markdown: string): string | null {
    /**
     * Rebuilds a 3-column repair table that Gemini flattened onto one line
     * by using estimated-cost cells as anchors between item and reason.
     */

    const headerMatch = markdown.match(TABLE_HEADER_PATTERN);
    if (!headerMatch || headerMatch.index === undefined) {
        return null;
    }

    const afterHeader = markdown.slice(headerMatch.index + headerMatch[0].length).trim();
    if (!afterHeader.includes("|")) {
        return null;
    }

    // locate each estimated-cost cell so item and reason can be sliced around it
    const costs: Array<{value: string; index: number; end: number}> = [];
    const costPattern = new RegExp(COST_CELL_PATTERN, "g");
    let costMatch: RegExpExecArray | null = costPattern.exec(afterHeader);
    while (costMatch) {
        costs.push({
            value: costMatch[1].replace(/\s+/g, ""),
            index: costMatch.index,
            end: costMatch.index + costMatch[0].length,
        });
        costMatch = costPattern.exec(afterHeader);
    }

    if (costs.length === 0) {
        return null;
    }

    // build one row per cost, splitting the reason from the following item title
    const rows: string[][] = [["Item", "Estimated Cost", "Why it's needed"]];
    let pendingItem = afterHeader.slice(0, costs[0].index).replace(/^\|+\s*/, "").trim();

    for (let i = 0; i < costs.length; i++) {
        const isLast = i === costs.length - 1;
        const afterCost = (isLast
            ? afterHeader.slice(costs[i].end)
            : afterHeader.slice(costs[i].end, costs[i + 1].index)
        ).replace(/\s*\|+\s*$/, "").trim();

        let why = afterCost;
        let nextItem = "";
        if (!isLast) {
            const split = splitWhyFromNextItem(afterCost);
            why = split.why;
            nextItem = split.nextItem;
        }

        if (pendingItem) {
            rows.push([pendingItem, costs[i].value, why]);
        }
        pendingItem = nextItem;
    }

    if (rows.length < 2) {
        return null;
    }

    const before = markdown.slice(0, headerMatch.index).trim();
    const table = toGfmTable(rows);
    return before ? `${before}\n\n${table}` : table;
}

// REBUILD FROM LINES
function rebuildFromLines(markdown: string): string {
    /**
     * Turns consecutive pipe-delimited lines that lack a GFM separator into
     * a proper markdown table, leaving surrounding prose unchanged.
     */

    const lines = markdown.split(/\r?\n/);
    const output: string[] = [];
    let tableLines: string[] = [];

    // flush consecutive pipe rows as one gfm table, or echo them unchanged

    const flushTable = (): void => {
        if (tableLines.length === 0) {
            return;
        }

        const rows = tableLines
            .filter((line) => !SEPARATOR_ROW_PATTERN.test(line))
            .map(splitCells)
            .filter((cells) => cells.length > 0);

        if (rows.length >= 2 && rows.every((row) => row.length >= 2)) {
            output.push(toGfmTable(rows));
        } else {
            output.push(...tableLines);
        }
        tableLines = [];
    };

    for (const line of lines) {
        if (line.includes("|") || (tableLines.length > 0 && SEPARATOR_ROW_PATTERN.test(line))) {
            tableLines.push(line);
            continue;
        }

        flushTable();
        output.push(line);
    }
    flushTable();

    return output.join("\n");
}

// NORMALIZE MARKDOWN TABLES
export function normalizeMarkdownTables(markdown: string): string {
    /**
     * Rewrites pipe-delimited repair tables into GitHub-flavoured markdown so
     * remark-gfm can render them. Gemini often omits the separator row and
     * sometimes the newlines between rows, which leaves the table as a single
     * paragraph of literal pipe text.
     */

    if (!markdown.includes("|")) {
        return markdown;
    }

    if (hasGfmSeparator(markdown)) {
        return markdown;
    }

    // rows with newlines only need a separator; a single line needs reconstruction
    const lineCount = markdown.split(/\r?\n/).filter((line) => line.includes("|")).length;
    if (lineCount >= 2) {
        return rebuildFromLines(markdown);
    }

    return reconstructConcatenatedTable(markdown) ?? markdown;
}
